import React, { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useApp } from "../context/AppContext";
import { inr } from "../lib/format";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import AuthModal from "./AuthModal";

const QUICK = [100, 500, 1000, 2500];
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
};

// Toss market — even money (1000 → 1000). Closes 1 hour before the scheduled start,
// result confirmed by admin after the Jitex toss feed. Exchange-style group layout.
export default function TossMarket({ match }) {
  const { isAuthed, balance, placeTossBet, tossBets } = useApp();
  const { toast } = useToast();
  const [team, setTeam] = useState(null);
  const [stake, setStake] = useState("");
  const [placing, setPlacing] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const tm = match.tossMarket;
  if (!tm || !tm.closeAt || !tm.open) return null;
  const teams = [match.homeTeam, match.awayTeam].filter(Boolean);
  const stakeNum = Number(stake) || 0;
  const mine = (tossBets || []).filter((b) => b.matchId === match.id && b.status === "open");
  const myStake = (code) => mine.filter((b) => b.teamId === code).reduce((t, b) => t + (Number(b.stake) || 0), 0);

  const pick = (code) => { setTeam(code); setStake(""); };
  const confirm = async () => {
    if (!isAuthed) { setAuthOpen(true); return; }
    if (!team) { toast({ title: "Pick a team first" }); return; }
    if (stakeNum <= 0) { toast({ title: "Enter a stake amount" }); return; }
    if (stakeNum > balance) { toast({ title: "Insufficient balance", description: `You need ${inr(stakeNum)} to place this bet.` }); return; }
    setPlacing(true);
    const res = await placeTossBet({ matchId: match.id, teamId: team, stake: stakeNum });
    setPlacing(false);
    if (res.ok) {
      toast({ title: `Toss bet placed ✅ · Ref ${res.ref || ""}`.trim(), description: `${teams.find((t) => t.code === team)?.name || team} to win the toss` });
      setTeam(null); setStake("");
    } else {
      toast({ title: "Could not place bet", description: res.error });
    }
  };

  return (
    <div className="rounded-[3px] overflow-hidden shadow-sm" data-testid="market-toss">
      <button onClick={() => setCollapsed((v) => !v)} className="w-full flex items-center justify-between bg-[#1f3b5b] text-white px-3 py-2 text-left">
        <span className="font-semibold text-[13px]">Toss Winner</span>
        <span className="flex items-center gap-2 text-[11px]" data-testid="toss-close-line">
          <span className="text-sky-200">Closes {fmtTime(tm.closeAt)}</span>
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </span>
      </button>
      {!collapsed && (
        <div className="bg-white">
          {teams.map((t) => {
            const st = myStake(t.code);
            const active = team === t.code;
            return (
              <div key={t.code} className="grid grid-cols-[1fr_84px] border-b border-slate-200">
                <div className="flex flex-col justify-center px-3 py-2 text-[13px] text-slate-800">
                  <span className="font-medium truncate">{t.name}</span>
                  {st > 0 && <span data-testid={`toss-${t.code}-stake`} className="text-[11px] font-semibold text-sky-700">{inr(st)}</span>}
                </div>
                <button data-testid={`toss-${t.code}-btn`} onClick={() => pick(t.code)} title={t.name}
                  className={`h-[46px] flex flex-col items-center justify-center leading-none transition-colors ${active ? "bg-[#5AAEEA] ring-2 ring-inset ring-sky-800" : "bg-[#72BBEF] hover:bg-[#5AAEEA]"}`}>
                  <span className="font-display font-extrabold text-[17px] text-slate-900">2.0</span>
                  <span className="text-[10px] text-slate-800/80 mt-0.5">Lagai</span>
                </button>
              </div>
            );
          })}

          {team && (
            <div className="p-2.5 space-y-2 bg-slate-50" data-testid="toss-slip">
              <div className="flex items-center justify-between text-sm text-slate-800">
                <span className="font-semibold">{teams.find((t) => t.code === team)?.name} <span className="text-sky-700">to win the toss</span></span>
                <span className="text-xs text-slate-500">Even money</span>
              </div>
              <Input type="number" inputMode="numeric" min={0} value={stake} onChange={(e) => setStake(e.target.value)} placeholder="Enter stake"
                className="h-10 text-base bg-white text-slate-900 border-slate-300" data-testid="toss-stake-input" />
              <div className="grid grid-cols-4 gap-1.5">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => setStake(String((Number(stake) || 0) + q))} className="rounded-sm bg-[#1f3b5b] text-white py-1.5 text-xs font-semibold active:scale-95 transition-transform" data-testid={`toss-quick-${q}`}>+{q}</button>
                ))}
              </div>
              <div className="flex justify-between text-sm border-t border-slate-200 pt-1.5 text-slate-700">
                <span>Return on win</span>
                <span className="font-display font-bold text-emerald-700" data-testid="toss-return">{inr(Math.round(stakeNum * 2))}</span>
              </div>
              <Button className="w-full h-10 text-sm font-bold text-black border-0 bg-[#f5b400] hover:bg-[#ffc21a]" onClick={confirm} disabled={placing} data-testid="toss-confirm">
                {isAuthed ? (placing ? "Placing…" : `Place Bet · ${inr(Math.round(stakeNum))}`) : "Login to bet"}
              </Button>
            </div>
          )}
        </div>
      )}
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
